<script lang="ts" setup>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { inject } from 'vue'

// Props
interface ModalProps {
  open: boolean
  unmount?: boolean
  size?: string
  bodyClass?: string
  loading?: boolean
  transitionDuration?: {
    enter?: number
    leave?: number
  }
}

const props = withDefaults(defineProps<ModalProps>(), {
  unmount: false,
  size: 'sm:max-w-lg',
  bodyClass: 'px-4 py-5 sm:px-6',
  loading: false,
  transitionDuration: () => ({
    enter: 300,
    leave: 200,
  }),
})

// Emits
const emits = defineEmits<{
  close: []
  afterClose: []
}>()

const isDark = inject<boolean>('isDark')

function handleClose() {
  emits('close')
}

function handleAfterLeave() {
  emits('afterClose')
}
</script>

<template>
  <TransitionRoot appear as="template" :show="props.open" @after-leave="handleAfterLeave">
    <Dialog
      as="div"
      class="relative"
      :style="{ zIndex: 'var(--z-modal)' }"
      :class="isDark ? 'dark' : ''"
      :unmount="props.unmount"
      @close="handleClose"
    >
      <!-- Modal Background -->
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 transition-opacity bg-gray-500/75 print:hidden dark:bg-gray-900/75"
        />
      </TransitionChild>

      <!-- Modal Panel -->
      <div class="overflow-y-auto fixed inset-0 print:static print:overflow-visible">
        <div
          class="flex justify-center items-end p-4 min-h-full text-center print:block print:min-h-0 print:p-0 print:text-left sm:items-center"
        >
          <TransitionChild
            as="template"
            :enter="`duration-${props.transitionDuration.enter} ease-out`"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            :leave="`duration-${props.transitionDuration.leave} ease-in`"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="overflow-hidden w-full antialiased text-left bg-white rounded-lg divide-y divide-gray-200 shadow-xl print:overflow-visible print:rounded-none print:border-0 print:shadow-none dark:divide-white/10 dark:bg-gray-800 dark:border-gray-700 dark:border"
              :class="[props.size, { 'opacity-50 pointer-events-none': props.loading }]"
            >
              <div v-if="$slots.header" class="px-4 py-5 sm:px-6">
                <slot name="header" />
              </div>

              <div :class="props.bodyClass">
                <slot />
              </div>

              <div v-if="$slots.footer" class="px-4 py-4 sm:px-6">
                <slot name="footer" />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
